/**
 * SHELL — install (A2HS) + update prompts.
 *
 * Captures the deferred `beforeinstallprompt` event and offers a tasteful,
 * dismissible "Add to Home Screen" card (respecting a one-time dismissal). Also
 * wires the vite-plugin-pwa update flow so a new build can refresh in place.
 */

import { div, el, button } from './dom';
import { icons } from './icons';
import { isInstallDismissed, dismissInstall } from '../storage/local';
import { isStandaloneDisplay, isNativeApp } from '../config';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let promptEl: HTMLElement | null = null;

function removePrompt(): void {
  promptEl?.remove();
  promptEl = null;
}

function showCard(text: string, actionLabel: string, onAction: () => void): void {
  removePrompt();
  promptEl = div('dr-prompt');
  promptEl.append(
    el('span', { html: icons.download, ariaLabel: '' }),
    el('div', { class: 'dr-prompt__text', text }),
    button('dr-btn dr-btn--primary', actionLabel, () => {
      onAction();
    }),
    button('dr-btn dr-btn--ghost', 'Later', () => {
      dismissInstall();
      removePrompt();
    }),
  );
  document.body.appendChild(promptEl);
}

/** Wire install + update prompts. Safe to call once at boot. */
export function setupInstallPrompts(): void {
  if (isNativeApp() || isStandaloneDisplay()) return; // already installed/native

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    if (isInstallDismissed()) return;
    showCard('Install DailyRoll for one-tap daily play.', 'Install', () => {
      void promptInstall();
    });
  });

  window.addEventListener('appinstalled', () => {
    deferred = null;
    removePrompt();
  });
}

/** Trigger the native install prompt if available. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferred) return 'unavailable';
  await deferred.prompt();
  const choice = await deferred.userChoice;
  deferred = null;
  removePrompt();
  return choice.outcome;
}

/** Whether an in-app "Install" affordance should be offered (deferred prompt held). */
export function canInstall(): boolean {
  return deferred !== null && !isStandaloneDisplay() && !isNativeApp();
}

/**
 * Register the PWA service worker + update flow. Returns a disposer. Uses the
 * virtual module from vite-plugin-pwa; no-op if the plugin isn't present.
 */
export async function registerServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const { registerSW } = await import('virtual:pwa-register');
    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        // Auto-apply new builds (no manual prompt) so every deploy is picked up on
        // the next load without the player getting stuck on a stale cached bundle.
        // updateSW(true) skips waiting and reloads to the fresh build.
        void updateSW(true);
      },
      onOfflineReady() {
        // Offline shell cached; no UI needed but we could toast here.
      },
    });
  } catch {
    /* plugin not in this build (e.g. dev) — ignore */
  }
}
